/**
 * Lightweight, homegrown device fingerprint — no third-party library.
 *
 * The real stability comes from a persisted random device-id in localStorage;
 * the hash just folds that together with coarse environment signals (UA,
 * platform, screen, timezone) into a compact, stable string. Degrades
 * gracefully: if localStorage is cleared or blocked, a fresh id is minted and
 * the device is treated as new — which is the intended privacy behaviour
 * (the account-aware login enhancement simply won't engage on an unknown
 * device).
 *
 * Used to (a) seed per-device login records for a future session-management UI
 * and (b) act as the "soft signal" gate for the email-first login lookup.
 */

const DEVICE_ID_KEY = "device_id";

/** Stable, persisted per-browser id. Minted on first use. */
export function getDeviceId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    // localStorage blocked (private mode / disabled) — no stable id available.
    return null;
  }
}

/** Synchronous 53-bit FNV-style hash, hex-encoded. Sufficient for device keys. */
function hashString(input: string): string {
  // cyrb53 — fast, well-distributed, non-cryptographic.
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < input.length; i++) {
    const ch = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const hash = 4294967296 * (2097151 & h2) + (h1 >>> 0);
  return hash.toString(16).padStart(14, "0");
}

/**
 * Compute the device fingerprint. Synchronous so it can be called inline from
 * getDeviceInfo() (which runs in form submit handlers). Returns undefined on
 * the server.
 */
export function computeFingerprint(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const deviceId = getDeviceId();
  if (!deviceId) return undefined; // no stable signal → treat as unknown device

  const screenSig =
    typeof screen !== "undefined"
      ? `${screen.width}x${screen.height}x${screen.colorDepth}`
      : "";
  let timeZone = "";
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
  } catch {
    // Intl unavailable — fine, just omit.
  }

  const parts = [
    navigator.userAgent ?? "",
    navigator.platform ?? "",
    screenSig,
    timeZone,
    deviceId,
  ];
  return hashString(parts.join("|"));
}
